import { ReactNode } from 'react';
import { Select } from '@ardoq/select';
import { Pagination } from './Pagination';
import { PaginationProps, ShowingResultsArgs } from './types';
import { noop, uniq } from 'lodash';
import { LeftSideText, PageSelectorText, RightSideText } from './atoms';
import { FlexBox } from '@ardoq/layout';

export const PAGINATION_BAR_HEIGHT = 52;

export type PaginationBarProps = {
  onChange?: (options: { currentPageNumber: number; perPage: number }) => void;
  pageSizeOptions?: number[];
  showPerPageSelector?: boolean;
  selectPerPageAriaLabel?: string;
  perPageSuffix?: ReactNode;
  renderShowingResults?: (args: ShowingResultsArgs) => ReactNode;
} & Omit<PaginationProps, 'onPageSelect'>;

const defaultRenderShowingResults = ({
  from,
  to,
  total,
  resultTypeLabel,
}: ShowingResultsArgs): ReactNode => (
  <>
    Showing{' '}
    <strong>
      {from}-{to}
    </strong>{' '}
    of <strong>{total}</strong> {resultTypeLabel ? `${resultTypeLabel} ` : ''}
    results
  </>
);

export const PaginationBar = (props: PaginationBarProps) => {
  const {
    resultTypeLabel,
    totalResults,
    currentPageNumber,
    perPage,
    isDisabled,
    onChange = noop,
    pageSizeOptions = [10, 25, 50, 100],
    showPerPageSelector = totalResults > Math.min(10, perPage),
    selectPerPageAriaLabel = 'Select results count per page',
    perPageSuffix = ' / page',
    renderShowingResults = defaultRenderShowingResults,
  } = props;
  const from = perPage * (currentPageNumber - 1) + 1;
  const to = perPage * currentPageNumber;

  // This condition will affect the display of pages count bar
  const showPagination = totalResults > perPage;

  if (totalResults) {
    const options = uniq([...pageSizeOptions, perPage]).map(num => ({
      label: `${num}`,
      value: num,
    }));

    return (
      <FlexBox flex={1} gap="medium" justify="space-between" align="center">
        <LeftSideText>
          {renderShowingResults({
            from,
            to: Math.min(to, totalResults),
            total: totalResults,
            resultTypeLabel,
          })}
        </LeftSideText>
        {showPagination && (
          <Pagination
            {...props}
            onPageSelect={selectedPage =>
              onChange({ currentPageNumber: selectedPage, perPage })
            }
          />
        )}
        <RightSideText>
          {showPerPageSelector && (
            <>
              <Select
                isDisabled={isDisabled}
                isSearchable={false}
                value={perPage}
                onValueChange={newPerPage =>
                  onChange({ currentPageNumber: 1, perPage: newPerPage })
                }
                options={options}
                aria-label={selectPerPageAriaLabel}
              />
              <PageSelectorText>{perPageSuffix}</PageSelectorText>
            </>
          )}
        </RightSideText>
      </FlexBox>
    );
  }

  return null;
};
